import { useState } from "react";
import { trpc } from "../../main/components/trpc.ts";

const formatRate = (rate: number) =>
  `${(rate * 100).toLocaleString("fr-FR", {
    maximumFractionDigits: 1,
  })} %`;

const formatOffsetLabel = (offsetDays: number) => `J-${offsetDays}`;

const OFFSET_LABELS: Record<number, string> = {
  60: "J-60",
  30: "J-30",
  21: "J-21",
  14: "J-14",
  7: "J-7",
  2: "J-2",
  0: "J-0 (lundi 8h)",
};

export default function ProspectiveFillStats() {
  const prospectiveFillStatsQuery = trpc.stats.getProspectiveFillStats.useQuery(
    undefined,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
  const [selectedOffset, setSelectedOffset] = useState(14);

  const offsets = prospectiveFillStatsQuery.data?.offsets;
  const availableOffsets = offsets?.map((offset) => offset.offsetDays) ?? [];
  const selectedOffsetStats =
    offsets?.find((offset) => offset.offsetDays === selectedOffset) ??
    offsets?.[0];

  return (
    <section style={{ marginBottom: "2rem" }}>
      <h2 style={{ marginBottom: "0.5rem", fontWeight: "bold", fontSize: "1.25rem" }}>
        Remplissage à priori (historique)
      </h2>
      <p style={{ marginBottom: "1rem" }}>
        Remplissage planifié de chaque semaine complète tel qu'il apparaissait à
        une date de référence antérieure (J-N = N jours avant le lundi de la
        semaine, à 8h). Ne sont comptés que les rdvs créés avant la date de
        référence et non encore annulés à cette date ; les rdvs Doctolib
        comptent, les cases "Réservé Doctolib" restent des disponibilités.
        Reconnaissance basée sur le journal de création des rdvs : les rdvs
        déplacés apparaissent à leur date finale et les rdvs supprimés
        définitivement sont invisibles.
      </p>
      {prospectiveFillStatsQuery.isLoading ? (
        <p>Calcul des statistiques...</p>
      ) : null}
      {prospectiveFillStatsQuery.error ? (
        <p role="alert" style={{ color: "#b91c1c", marginTop: "0.5rem" }}>
          Impossible de calculer les statistiques :{" "}
          {prospectiveFillStatsQuery.error.message}
        </p>
      ) : null}
      {offsets && offsets.length > 0 ? (
        <label style={{ display: "block", marginBottom: "1rem" }}>
          Date de référence :{" "}
          <select
            value={selectedOffsetStats?.offsetDays ?? selectedOffset}
            onChange={(event) =>
              setSelectedOffset(Number(event.target.value))
            }
          >
            {availableOffsets.map((offsetDays) => (
              <option key={offsetDays} value={offsetDays}>
                {OFFSET_LABELS[offsetDays] ?? formatOffsetLabel(offsetDays)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {selectedOffsetStats && selectedOffsetStats.yearStats.length > 0 ? (
        <div style={{ marginTop: "1rem", overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", minWidth: "800px", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "0.5rem" }}>Année</th>
                <th style={{ textAlign: "right", padding: "0.5rem" }}>Semaines considérées</th>
                <th style={{ textAlign: "right", padding: "0.5rem" }}>Moyenne</th>
                <th style={{ textAlign: "right", padding: "0.5rem" }}>Médiane</th>
                <th style={{ textAlign: "right", padding: "0.5rem" }}>25e percentile</th>
                <th style={{ textAlign: "right", padding: "0.5rem" }}>10e percentile</th>
                <th style={{ textAlign: "right", padding: "0.5rem" }}>Semaines à moins de 70 %</th>
              </tr>
            </thead>
            <tbody>
              {selectedOffsetStats.yearStats.map((yearStats) => (
                <tr key={yearStats.year}>
                  <td style={{ padding: "0.5rem" }}>{yearStats.year}</td>
                  <td style={{ textAlign: "right", padding: "0.5rem" }}>
                    {yearStats.weekCount}
                  </td>
                  <td style={{ textAlign: "right", padding: "0.5rem" }}>
                    {formatRate(yearStats.meanFillRate)}
                  </td>
                  <td style={{ textAlign: "right", padding: "0.5rem" }}>
                    {formatRate(yearStats.medianFillRate)}
                  </td>
                  <td style={{ textAlign: "right", padding: "0.5rem" }}>
                    {formatRate(yearStats.p25FillRate)}
                  </td>
                  <td style={{ textAlign: "right", padding: "0.5rem" }}>
                    {formatRate(yearStats.p10FillRate)}
                  </td>
                  <td style={{ textAlign: "right", padding: "0.5rem" }}>
                    {yearStats.weeksBelow70Percent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
